import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FormData } from "@/components/AdvisorWizard";
import { Json } from "@/integrations/supabase/types";

// Cryptographically secure session id
const generateSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `session_${crypto.randomUUID()}`;
  }
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return (
    "session_" +
    Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("")
  );
};

export const useWizardTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  const hasInsertedRef = useRef(false);

  // Initialize a session id locally (no DB write yet to avoid orphan rows
  // and because anonymous users can no longer update existing rows).
  const initSession = useCallback(async () => {
    if (isInitializedRef.current) return sessionIdRef.current;
    sessionIdRef.current = generateSessionId();
    isInitializedRef.current = true;
    return sessionIdRef.current;
  }, []);

  // No-op for intermediate steps. Anonymous UPDATE is no longer permitted by
  // RLS for security reasons, so we only persist the final completed session.
  const trackStep = useCallback(
    async (_step: string, _formData: Partial<FormData>) => {
      if (!sessionIdRef.current) {
        await initSession();
      }
    },
    [initSession]
  );

  // Persist the completed wizard session as a single INSERT.
  const trackCompletion = useCallback(
    async (formData: FormData, recommendations: Json) => {
      if (!sessionIdRef.current) {
        await initSession();
      }
      if (hasInsertedRef.current) return;
      hasInsertedRef.current = true;

      try {
        await supabase.from("wizard_sessions").insert({
          session_id: sessionIdRef.current!,
          current_step: "results",
          completed_at: new Date().toISOString(),
          recommendations: recommendations,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          age_range: formData.age,
          family_status: formData.familyStatus,
          financial_situation: formData.financialSituation,
          main_reason: formData.mainReason,
          property_type: formData.propertyType,
          property_value: formData.estimatedValue,
          location: formData.location,
          payment_form: formData.paymentForm,
          priority: formData.priority,
          monthly_payment: formData.monthlyPayments,
          future_plans: formData.futurePlans,
        });
      } catch (error) {
        console.error("Error tracking wizard completion:", error);
      }
    },
    [initSession]
  );

  const resetTracking = useCallback(() => {
    sessionIdRef.current = null;
    isInitializedRef.current = false;
    hasInsertedRef.current = false;
  }, []);

  return {
    initSession,
    trackStep,
    trackCompletion,
    resetTracking,
  };
};
